/**
 * <HijriDayButton> — react-day-picker v9 DayButton override.
 *
 * Renders Gregorian day number (primary) above Hijri day number (secondary,
 * smaller + muted + mono) inside every day cell of the DateField popup
 * calendar grid. Gregorian stays primary because users still type + think
 * in Gregorian for form INPUT; the Hijri label is visible reference.
 *
 * Interoperability with react-day-picker v9 (Pitfall 8):
 *   - DO NOT import from `react-day-picker/hijri`. That entrypoint swaps
 *     the PRIMARY calendar to Hijri — exactly the opposite of what we want
 *     for form entry. We stay on the default Gregorian entry and overlay
 *     Hijri as secondary text inside each day cell.
 *   - rdp v9's default DayButton is:
 *       `({ day, modifiers, ...buttonProps }) => <button ref={ref} {...buttonProps} />`
 *     where the internal `ref` focuses the button when `modifiers.focused`
 *     flips to true (that's how arrow-key roving tabindex works).
 *   - We replicate that effect so keyboard navigation (arrow keys / Home /
 *     End / PageUp / PageDown) continues to focus the correct cell after
 *     customization. The `{...buttonProps}` spread carries onClick,
 *     onKeyDown, aria-*, tabIndex, disabled, and data-* from rdp core.
 *
 * Hijri conversion: native `Intl.DateTimeFormat` with `islamic-umalqura`
 * ONLY (DS-06 / Pitfall C10). No third-party Hijri libs imported anywhere.
 *
 * @example
 *   <DayPicker components={{ DayButton: HijriDayButton }} ... />
 */
import { type ButtonHTMLAttributes, useEffect, useRef } from "react";
import type { CalendarDay, Modifiers } from "react-day-picker";
import { cn } from "../../utils";

/**
 * Formats a single Date as the Hijri day-of-month numeral (e.g. "5", "29").
 *
 * `numberingSystem`: `"latn"` for Western 0-9 digits (v1 default per
 * I18N-07), `"arab"` for Arabic-Indic ٠-٩. The locale stub is `en-US`
 * regardless of numbering system because we only render the day numeral —
 * month names come from rdp's own locale handling on the caption row.
 */
export function hijriDayNumber(date: Date, numberingSystem: "latn" | "arab" = "latn"): string {
  return new Intl.DateTimeFormat(`en-US-u-ca-islamic-umalqura-nu-${numberingSystem}`, {
    day: "numeric",
  }).format(date);
}

/**
 * Props shape matches react-day-picker v9 DayButton signature exactly
 * (Parameters<typeof DayButton>[0] from rdp's type exports):
 *
 *   { day: CalendarDay, modifiers: Modifiers } & ButtonHTMLAttributes<HTMLButtonElement>
 *
 * Consumers wiring via `<DayPicker components={{ DayButton: HijriDayButton }} />`
 * will get exact-shape inference; we add an optional `numberingSystem` knob
 * for consumers who render the override directly.
 */
export interface HijriDayButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  day: CalendarDay;
  modifiers: Modifiers;
  /** Numeral system for the Hijri sub-label. Default `latn` (I18N-07). */
  numberingSystem?: "latn" | "arab";
}

export function HijriDayButton({
  day,
  modifiers,
  numberingSystem = "latn",
  className,
  children: _children, // rdp passes the default Gregorian child; we replace it.
  ...buttonProps
}: HijriDayButtonProps) {
  // Mirror rdp's default DayButton: focus the button when modifiers.focused
  // flips. Without this, arrow-key navigation stops announcing the active
  // cell to screen readers + visually loses the roving tabindex.
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  const gregDay = day.date.getDate();
  const hjDay = hijriDayNumber(day.date, numberingSystem);

  return (
    <button
      ref={ref}
      {...buttonProps}
      // `type="button"` prevents accidental form submission from an
      // enclosing <form> if DateField is ever nested inside one.
      type="button"
      className={cn(
        "flex flex-col items-center justify-center rounded-md transition-colors",
        "hover:bg-surface-sunken",
        "focus-visible:focus-ring focus-visible:outline-none",
        modifiers.selected ? "bg-primary text-on-primary" : "text-default",
        modifiers.today ? "font-semibold" : "",
        modifiers.disabled ? "opacity-50 cursor-not-allowed" : "",
        className,
      )}
    >
      <span className="text-body leading-none">{gregDay}</span>
      <span
        className="text-muted leading-none"
        style={{
          fontFamily: "var(--font-family-mono)",
          fontSize: "10px",
          letterSpacing: "0.02em",
          marginBlockStart: "2px",
        }}
        aria-hidden="true"
      >
        {hjDay}
      </span>
    </button>
  );
}
